import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Observable} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {DateService} from '../services/date/date.service';
import {LanguageService} from '../services/language/language.service';
import {SimulatedDateService} from '../services/simulated-date/simulated-date.service';
import {CmsResponse, Post, PostDetail, ReleaseNote, ReleaseNoteDetail, Stoerungsmeldung, StoerungsmeldungDetail, Tipp, TippDetail} from './cms-types';

type CmsCollection = 'release-notes' | 'stoerungsmeldungen' | 'tipps';
type CmsStatus = 'draft' | 'published';

@Injectable({
  providedIn: 'root'
})
export class CmsService {
  private now = DateService.dateTimeToUTCDateTime(this.simulatedDate.getSimulatedDate());
  private cutoffDate = this.getCutoffUTCDateTime();

  private currentReleaseNotes$ = this.getCollection<ReleaseNote>('release-notes', {
    'filters[AktivAb][$lte]': this.now,
    'filters[AktivBis][$gte]': this.now,
    status: this.getStatus()
  }).pipe(
    map(notes => notes[0]),
    shareReplay(1)
  );

  constructor(
    private httpClient: HttpClient,
    private simulatedDate: SimulatedDateService,
    private languageService: LanguageService,
    private activatedRoute: ActivatedRoute
  ) {}

  public getCurrentReleaseNote(): Observable<ReleaseNote | undefined> {
    return this.currentReleaseNotes$;
  }

  public getReleaseNoteById(documentId: string): Observable<ReleaseNoteDetail> {
    return this.getDocumentDetail<ReleaseNoteDetail>('release-notes', documentId);
  }

  /** return all past and active release notes, ordered newest first */
  getAllReleaseNotes(): Observable<ReleaseNote[]> {
    return this.getCollection<ReleaseNote>('release-notes', {
      'filters[AktivAb][$lte]': this.now,
      status: this.getStatus(),
      sort: 'AktivAb:desc'
    });
  }

  public getStoerungsmeldungById(documentId: string): Observable<StoerungsmeldungDetail> {
    return this.getDocumentDetail<StoerungsmeldungDetail>('stoerungsmeldungen', documentId);
  }

  /** return all currently active stoerungsmeldungen */
  public getActiveStoerungsmeldungen(): Observable<Stoerungsmeldung[]> {
    return this.getCollection<Stoerungsmeldung>('stoerungsmeldungen', {
      'filters[AktivAb][$lte]': this.now,
      'filters[AktivBis][$gte]': this.now,
      status: this.getStatus(),
      sort: 'AktivAb:desc'
    });
  }

  /** return all past and active stoerungsmeldungen, ordered newest first */
  getAllStoerungsmeldungen(): Observable<Stoerungsmeldung[]> {
    return this.getCollection<Stoerungsmeldung>('stoerungsmeldungen', {
      'filters[AktivAb][$lte]': this.now,
      'filters[AktivBis][$gte]': this.cutoffDate,
      status: this.getStatus(),
      sort: 'AktivAb:desc'
    });
  }

  /** return all past and active tipps, ordered newest first */
  public getAllTipps(): Observable<Tipp[]> {
    return this.getCollection<Tipp>('tipps', {
      'filters[AktivAb][$lte]': this.now,
      status: this.getStatus(),
      sort: 'AktivAb:desc'
    });
  }

  /** return all currently active tipps */
  public getActiveTipps(): Observable<Tipp[]> {
    return this.getCollection<Tipp>('tipps', {
      'filters[AktivAb][$lte]': this.now,
      'filters[AktivBis][$gte]': this.now,
      status: this.getStatus(),
      sort: 'AktivAb:desc'
    });
  }

  public getTippById(documentId: string): Observable<TippDetail> {
    return this.getDocumentDetail<TippDetail>('tipps', documentId);
  }

  private getCollection<TDocument extends Post>(collection: CmsCollection, params: Record<string, string>): Observable<TDocument[]> {
    return this.httpClient
      .get<CmsResponse<TDocument[]>>(`${environment.cms.url}/api/${collection}`, {
        headers: this.headers,
        params: {...params, locale: this.locale}
      })
      .pipe(map(response => response.data));
  }

  private getDocumentDetail<TDocument extends PostDetail>(collection: CmsCollection, documentId: string): Observable<TDocument> {
    return this.httpClient
      .get<CmsResponse<TDocument>>(`${environment.cms.url}/api/${collection}/${documentId}`, {
        headers: this.headers,
        params: {
          'populate[Langcontent][populate]': '*',
          locale: this.locale,
          status: this.getStatus()
        }
      })
      .pipe(map(response => response.data));
  }

  private get headers(): Record<string, string> {
    return {Authorization: `Bearer ${environment.cms.token}`};
  }

  private get locale(): string {
    return `${this.languageService.lang}-CH`;
  }

  private getStatus(): CmsStatus {
    return this.activatedRoute.snapshot.queryParams['debug'] === 'preview' ? 'draft' : 'published';
  }

  private getCutoffUTCDateTime() {
    const today = this.simulatedDate.getSimulatedDate();
    const cutoffDate = DateService.addDaysToDate(today, -2);
    cutoffDate.setHours(0, 0, 0, 0);
    return DateService.dateTimeToUTCDateTime(cutoffDate);
  }
}
